import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Plus, PaperPlaneRight, UsersThree, EnvelopeSimple, Paperclip, Clock } from "@phosphor-icons/react";

const CATEGORIES = ["Memo", "Promotion", "Announcement", "Reminder", "Other"];
const CATEGORY_BADGE = {
  Memo: "bg-primary/10 text-primary border-primary/20",
  Promotion: "bg-success/10 text-success border-success/20",
  Announcement: "bg-warning/10 text-warning border-warning/20",
  Reminder: "bg-accent text-foreground border-border",
  Other: "bg-muted text-muted-foreground border-border",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageHeader() {
  return (
    <div>
      <p className="uppercase text-xs tracking-[0.25em] text-primary font-semibold mb-1">Announcements</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Notices &amp; Memos</h1>
      <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
        <Megaphone className="h-4 w-4" /> Company notices are posted here and emailed to recipients.
      </p>
    </div>
  );
}

export default function Memos() {
  const { isAdmin } = useAuth();
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/memos");
      setMemos(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (isAdmin) api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [load, isAdmin]);

  return (
    <div className="space-y-6" data-testid="memos-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader />
        {isAdmin && (
          <Button onClick={() => setOpen(true)} data-testid="new-memo-button" className="rounded-lg gap-2 font-semibold">
            <Plus weight="bold" className="h-4 w-4" /> New Memo
          </Button>
        )}
      </div>

      {loading ? (
        <p className="py-16 text-center text-muted-foreground">Loading...</p>
      ) : memos.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="memos-empty">
          <Megaphone className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{isAdmin ? "No memos sent yet. Click \"New Memo\" to send one." : "No memos yet."}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {memos.map((m) => (
            <Card key={m.id} className="p-5 border-border shadow-none animate-fade-up" data-testid={`memo-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Badge className={`mb-1 ${CATEGORY_BADGE[m.category] || CATEGORY_BADGE.Memo}`} data-testid={`memo-category-${m.id}`}>{m.category || "Memo"}</Badge>
                  <h3 className="font-heading font-bold text-lg">{m.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {m.created_by_name} · {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {m.status === "scheduled" ? (
                      <>
                        <Badge className="bg-warning/10 text-warning border-warning/20 gap-1" data-testid={`memo-scheduled-${m.id}`}>
                          <Clock className="h-3.5 w-3.5" /> Scheduled
                        </Badge>
                        <span className="text-[11px] text-muted-foreground">{m.send_at ? new Date(m.send_at).toLocaleString() : ""}</span>
                      </>
                    ) : (
                      <>
                        <Badge className="bg-primary/10 text-primary border-primary/20 gap-1">
                          <UsersThree className="h-3.5 w-3.5" /> {m.recipient_count} recipient{m.recipient_count === 1 ? "" : "s"}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <EnvelopeSimple className="h-3 w-3" /> {m.emailed_count} emailed
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <p className="text-sm mt-3 whitespace-pre-wrap text-foreground/90">{m.body}</p>
              {m.attachment_id && (
                <a href={`${API}/memos/attachment/${m.attachment_id}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-primary hover:underline"
                  data-testid={`memo-attachment-${m.id}`}>
                  <Paperclip className="h-4 w-4" /> {m.attachment_name || "Download attachment"}
                </a>
              )}
              {isAdmin && m.audience !== "all" && (
                <p className="text-[11px] text-muted-foreground mt-3">
                  Audience: {m.audience === "department" ? `Department · ${m.department}` : "Selected employees"}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {isAdmin && <MemoDialog open={open} onOpenChange={setOpen} employees={employees} onSent={load} />}
    </div>
  );
}

function MemoDialog({ open, onOpenChange, employees, onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("Memo");
  const [audience, setAudience] = useState("all");
  const [department, setDepartment] = useState("");
  const [selected, setSelected] = useState([]);
  const [file, setFile] = useState(null);
  const [scheduleAt, setScheduleAt] = useState("");
  const [busy, setBusy] = useState(false);

  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))];

  const reset = () => {
    setTitle(""); setBody(""); setCategory("Memo"); setAudience("all"); setDepartment(""); setSelected([]);
    setFile(null); setScheduleAt("");
  };

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and message are required"); return; }
    if (audience === "department" && !department) { toast.error("Select a department"); return; }
    if (audience === "specific" && selected.length === 0) { toast.error("Select at least one employee"); return; }
    setBusy(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/memos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.attachment_id;
      }
      const send_at = scheduleAt ? new Date(scheduleAt).toISOString() : null;
      const { data } = await api.post("/memos", {
        title: title.trim(),
        body: body.trim(),
        category,
        audience,
        department: audience === "department" ? department : "",
        employee_ids: audience === "specific" ? selected : null,
        send_at,
        attachment_id,
      });
      if (data.status === "scheduled") {
        toast.success(`Notice scheduled for ${new Date(data.send_at).toLocaleString()}`);
      } else {
        toast.success(`Notice sent to ${data.recipient_count} recipient(s) · ${data.emailed_count} emailed`);
      }
      reset();
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="memo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Memo / Notice</DialogTitle>
          <DialogDescription>Posts to the portal and emails the selected recipients.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="memo-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="memo-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Office closed on June 12" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea data-testid="memo-body" value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Write your announcement here..." className="min-h-[120px]" />
          </div>
          <div className="space-y-1.5">
            <Label>Send to</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="memo-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="department">A department</SelectItem>
                <SelectItem value="specific">Specific employees</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {audience === "department" && (
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger data-testid="memo-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {departments.length === 0 ? (
                    <SelectItem value="none" disabled>No departments found</SelectItem>
                  ) : departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {audience === "specific" && (
            <div className="space-y-1.5">
              <Label>Employees ({selected.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="memo-employee-list">
                {employees.map((e) => (
                  <label key={e.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent"
                    data-testid={`memo-emp-${e.id}`}>
                    <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggle(e.id)}
                      className="h-4 w-4 accent-primary" />
                    <span className="text-sm">{e.name} <span className="text-muted-foreground">· {e.email}</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Attachment (optional)</Label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
              data-testid="memo-attachment" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1.5 file:text-sm file:font-semibold file:cursor-pointer" />
            {file && <p className="text-xs text-muted-foreground">Selected: {file.name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Schedule send (optional)</Label>
            <Input type="datetime-local" data-testid="memo-schedule" value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)} />
            <p className="text-xs text-muted-foreground">Leave empty to send immediately.</p>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={busy} data-testid="memo-send-button" className="gap-2 font-semibold">
            <PaperPlaneRight weight="bold" className="h-4 w-4" /> {busy ? "Sending..." : "Send memo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
